import { forwardRef, type ElementType, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export interface GlassPanelProps extends HTMLAttributes<HTMLElement> {
  /** Qué elemento pinta. `div` por defecto; `aside`, `nav`, `header`… */
  as?: ElementType;
  children: ReactNode;
}

/**
 * La superficie de vidrio del sistema, sola.
 *
 * Es EXACTAMENTE la fórmula que ya aprobó Anahí en las pantallas de
 * acceso —`AuthCard`—: `rounded-3xl`, borde tenue, blanco translúcido,
 * `backdrop-blur-2xl` y una sombra grande y suave.
 *
 * Se extrajo porque el armazón la necesita en cuatro sitios (barra
 * lateral, barra superior, cajón móvil y el menú de la cuenta) y
 * `AuthCard` no servía: esa card trae logotipo, título y pie, que aquí no
 * pintan nada. Esto es la SUPERFICIE; `AuthCard` es una composición sobre
 * ella. No es un gemelo de `AuthCard`: es la capa que le faltaba debajo.
 *
 * ── El brillo diagonal se retiró, y con él la prop `sheen` ───────────
 * Había una capa absoluta encima con `bg-gradient-to-br from-white/50 via-
 * transparent to-ds-mist/50` (y una variante `soft`, vertical, para los
 * paneles altos donde la diagonal se leía como una banda gris). Los dos
 * eran degradados decorativos sobre una superficie, que es justo lo que
 * Anahi retiró del sistema el 26-ago.
 *
 * Lo que hacían de verdad era subir el blanco: la diagonal empezaba en
 * white/50 sobre un fondo de white/35, así que la esquina de arriba
 * quedaba cerca de un white/67 y el centro se quedaba en el 35 % pelado.
 * Eso se consigue con UN número: el fondo sube de `bg-white/35` a
 * `bg-white/45`, que cae dentro de ese rango y es igual en todo el panel.
 * El volumen sigue viniendo de donde venía de verdad —el desenfoque de
 * fondo, el borde y la sombra—, no de la diagonal.
 *
 * Con la capa fuera, `sheen` no tenía nada que elegir y se fue también.
 * Una prop que solo puede valer "none" es ruido en la API.
 *
 * (Los hijos siguen necesitando `relative` en quien lo use: el panel crea
 * su propio contexto de apilado y el contenido va por encima del fondo.)
 */
export const GlassPanel = forwardRef<HTMLElement, GlassPanelProps>(function GlassPanel(
  { as, className, children, ...rest },
  ref,
) {
  const Element = (as ?? "div") as ElementType;

  return (
    <Element
      ref={ref}
      className={cn(
        "relative rounded-3xl border border-ds-border bg-white/45 backdrop-blur-2xl",
        "shadow-[0_25px_60px_rgba(0,0,0,0.08)]",
        className,
      )}
      {...rest}
    >
      {children}
    </Element>
  );
});
